/**
 * Community Page Module
 * Handles routing, state management, and rendering for the Community feature
 */
(function() {
  'use strict';

  function handleize(s) {
    return (s || '')
      .toString()
      .toLowerCase()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function decodeHtmlEntities(encodedStr) {
    if (!encodedStr || typeof encodedStr !== 'string') return '';
    var txt = document.createElement('textarea');
    txt.innerHTML = encodedStr;
    return txt.value;
  }

  function normalizeToHTML(input) {
    // input -> actual HTML string; handles: AST objects, stringified AST JSON strings, HTML strings, plain text
    if (!input) return '';
    if (typeof input === 'object') {
      if (input.type === 'root' && Array.isArray(input.children)) {
        return richTextASTToHTML(input);
      }
      return '';
    }
    if (typeof input !== 'string') return '';
    var str = input.trim();
    if (str.length === 0) return '';

    // Detect STRINGIFIED Rich Text AST JSON (common bug when | json serializes a Rich Text drop instead of HTML)
    //   e.g. {"type":"root","children":[...]}
    var firstBrace = str.indexOf('{');
    if (firstBrace === 0 || (firstBrace !== -1 && firstBrace < 20)) {
      var maybeJson = str.substring(firstBrace);
      // Must contain type, children keys to be AST, not just random JSON
      if (maybeJson.includes('"type"') && maybeJson.includes('"children"') && /"type"\s*:\s*"root"/.test(maybeJson.substring(0, 200))) {
        try {
          var ast = JSON.parse(maybeJson);
          if (ast && ast.type === 'root' && Array.isArray(ast.children)) {
            return richTextASTToHTML(ast);
          }
        } catch (e) {
          // Not valid JSON, continue with below HTML checkers
        }
      }
    }

    // If it starts with HTML chars or entities, treat as HTML (we'll entities-decode if needed)
    if (str.charAt(0) === '<' || str.includes('&lt;')) {
      // Decode double-escaped HTML entities
      var decoded = decodeHtmlEntities(str);
      if (decoded.trim().charAt(0) === '<') return decoded;
      if (str.trim().charAt(0) === '<') return str;
    }

    // Plain text — wrap in a paragraph
    return '<p>' + input + '</p>';
  }

  function isShopifyRichTextAST(value) {
    if (!value) return false;
    if (typeof value === 'string') {
      var trimmed = value.trim();
      if (trimmed.length === 0) return false;
      var first = trimmed.charAt(0);
      if (first === '{' || trimmed.startsWith('&#123;') || trimmed.startsWith('%7B')) {
        return (trimmed.includes('"type"') && trimmed.includes('"children"'));
      }
      return false;
    }
    if (typeof value === 'object') {
      return (value.type === 'root' && Array.isArray(value.children));
    }
    return false;
  }

  function richTextASTToHTML(ast) {
    if (!ast) return '';
    var root = ast;
    if (typeof ast === 'string') {
      try { root = JSON.parse(ast); }
      catch (e) { return ast; }
    }
    if (!root || !root.children || !Array.isArray(root.children)) return '';

    var html = '';
    function renderNode(node) {
      if (!node) return '';
      var out = '';
      if (node.type === 'root') {
        if (node.children) node.children.forEach(function(c) { out += renderNode(c); });
        return out;
      }
      if (node.type === 'paragraph') {
        var inner = '';
        if (node.children) node.children.forEach(function(c) { inner += renderNode(c); });
        out += '<p>' + inner + '</p>';
        return out;
      }
      if (node.type === 'text') {
        var txt = (node.value !== undefined) ? String(node.value) : '';
        var strong = (node.bold || node.strong) ? true : false;
        var em = (node.italic || node.em) ? true : false;
        if (strong) txt = '<strong>' + txt + '</strong>';
        if (em) txt = '<em>' + txt + '</em>';
        return txt;
      }
      if (node.type === 'heading' || node.type === 'h1' || node.type === 'h2' || node.type === 'h3' || node.type === 'h4' || node.type === 'h5' || node.type === 'h6') {
        var level = node.level || 1;
        if (node.type.charAt && node.type.charAt(0) === 'h') level = parseInt(node.type.charAt(1), 10) || 1;
        var headingInner = '';
        if (node.children) node.children.forEach(function(c) { headingInner += renderNode(c); });
        out += '<h' + level + '>' + headingInner + '</h' + level + '>';
        return out;
      }
      if (node.type === 'list' || node.type === 'ordered-list' || node.type === 'unordered-list') {
        var tag = (node.type === 'ordered-list') ? 'ol' : 'ul';
        var listInner = '';
        if (node.children) node.children.forEach(function(c) { listInner += renderNode(c); });
        out += '<' + tag + '>' + listInner + '</' + tag + '>';
        return out;
      }
      if (node.type === 'list-item') {
        var liInner = '';
        if (node.children) node.children.forEach(function(c) { liInner += renderNode(c); });
        out += '<li>' + liInner + '</li>';
        return out;
      }
      if (node.type === 'link') {
        var href = node.url || node.href || '#';
        var linkInner = '';
        if (node.children) node.children.forEach(function(c) { linkInner += renderNode(c); });
        out += '<a href="' + href + '">' + linkInner + '</a>';
        return out;
      }
      if (node.type === 'image') {
        var src = node.url || node.src || '';
        var alt = node.alt || (node.alt_text !== undefined ? node.alt_text : '');
        out += '<img src="' + src + '" alt="' + alt + '" loading="lazy">';
        return out;
      }
      if (node.type === 'blockquote') {
        var bqInner = '';
        if (node.children) node.children.forEach(function(c) { bqInner += renderNode(c); });
        out += '<blockquote>' + bqInner + '</blockquote>';
        return out;
      }
      if (node.children) {
        node.children.forEach(function(c) { out += renderNode(c); });
      }
      return out;
    }
    html = renderNode(root);
    return html;
  }

  function resolvePostContentHTML(post) {
    var slug = handleize(post.slug);
    var result = {
      html: '',
      galleryImages: [],
      sourceUsed: 'none'
    };

    var raw = { tier1_hidden_div: '', tier2_escaped: '', tier3_ast_or_obj: '', tier4_content_field: '' };

    // 1) PRIMARY: clone from server-rendered hidden divs (100% correct, no AST bug)
    var htmlDiv = document.getElementById('post-html-' + slug);
    if (htmlDiv) {
      raw.tier1_hidden_div = htmlDiv.innerHTML || '';
      var normalized = normalizeToHTML(raw.tier1_hidden_div);
      if (normalized.length > 0) {
        result.html = normalized;
        result.sourceUsed = 'hidden-div';
      }
    }
    var galleryDiv = document.getElementById('post-gallery-' + slug);
    if (galleryDiv) {
      var imgs = galleryDiv.querySelectorAll('img');
      imgs.forEach(function(img) {
        if (!img) return;
        var src = img.getAttribute('src') || img.src || img.getAttribute('data-src');
        if (src) result.galleryImages.push(src);
      });
    }

    // 2) SECONDARY: Liquid escaped HTML string (drop refs broken) -> decode entities
    if (!result.html && post.content_html_escaped && typeof post.content_html_escaped === 'string' && post.content_html_escaped.trim().length > 0) {
      raw.tier2_escaped = decodeHtmlEntities(post.content_html_escaped);
      var n2 = normalizeToHTML(raw.tier2_escaped);
      if (n2.length > 0) {
        result.html = n2;
        result.sourceUsed = 'content_html_escaped';
      }
    }

    // 3) TERTIARY: AST renderer (if content is Rich Text AST JSON/object)
    if (!result.html) {
      raw.tier3_ast_or_obj = (typeof post.content === 'string') ? post.content.substring(0, 300) : ((post.content && post.content.type) ? '[AST object ' + post.content.type + ']' : 'null');
      var n3 = normalizeToHTML(post.content);
      if (n3.length > 0) {
        result.html = n3;
        result.sourceUsed = 'ast-rendered';
      }
    }

    // 4) QUARTARY: content is already HTML string (plain)
    if (!result.html && post.content && typeof post.content === 'string' && post.content.trim().length > 0) {
      raw.tier4_content_field = post.content.substring(0, 300);
      var n4 = normalizeToHTML(post.content);
      if (n4.length > 0) {
        result.html = n4;
        result.sourceUsed = 'content-field';
      }
    }

    // Gallery: fall back to postsData.gallery + joined if needed
    if (result.galleryImages.length === 0 && post.gallery_urls_joined && typeof post.gallery_urls_joined === 'string') {
      result.galleryImages = post.gallery_urls_joined.split('|||').map(function(u) { return (u || '').trim(); }).filter(Boolean);
      if (result.galleryImages.length > 0 && result.sourceUsed === 'none') result.sourceUsed = 'gallery-joined';
    }
    if (result.galleryImages.length === 0 && post.gallery && Array.isArray(post.gallery)) {
      result.galleryImages = post.gallery.filter(function(u) { return u && typeof u === 'string' && u.trim().length > 0; });
      if (result.galleryImages.length > 0 && result.sourceUsed === 'none') result.sourceUsed = 'gallery-array';
    }

    console.groupCollapsed('[CommunityPage] resolvePostContentHTML(' + slug + ')');
    console.log('sourceUsed:', result.sourceUsed);
    console.log('final HTML length:', result.html.length, '| gallery count:', result.galleryImages.length, '| images =', result.galleryImages);
    console.log('tier1 (hidden div) raw length:', (raw.tier1_hidden_div || '').length, '| first 200 chars:', JSON.stringify((raw.tier1_hidden_div || '').substring(0, 200)));
    console.log('tier2 (escaped) decoded length:', (raw.tier2_escaped || '').length, '| first 200 chars:', JSON.stringify((raw.tier2_escaped || '').substring(0, 200)));
    console.log('tier3 (ast) raw:', raw.tier3_ast_or_obj);
    console.log('tier4 (content field) raw:', raw.tier4_content_field);
    console.log('postsData.gallery[] fallback:', post.gallery ? post.gallery : null, '| count:', post.gallery ? post.gallery.length : 0);
    console.log('postsData.gallery_urls_joined fallback:', post.gallery_urls_joined, '| raw_count:', post.gallery_debug_size_raw, '| debug_list:', post.gallery_debug_list_raw);
    console.log('final HTML (first 400):', JSON.stringify(result.html.substring(0, 400)));
    console.groupEnd();

    return result;
  }

  window.CommunityPage = {
    posts: [],
    currentPost: null,
    currentCategory: null,
    elements: {
      grid: null,
      detail: null,
      communitySidebarTitle: null,
      communitySidebarPost: null,
      categoriesContainer: null
    },

    init: function(posts) {
      this.posts = posts;
      this.elements.grid = document.getElementById('community-grid');
      this.elements.detail = document.getElementById('community-detail');
      this.elements.communitySidebarTitle = document.getElementById('community-sidebar-title');
      this.elements.communitySidebarPost = document.getElementById('community-sidebar-post');
      this.elements.categoriesContainer = document.getElementById('community-categories-container');

      console.log('[CommunityPage] init() postsData =', posts);
      if (posts && posts.length > 0) {
        console.log('[CommunityPage] first post keys =', Object.keys(posts[0]));
        console.log('[CommunityPage] first post content_blocks =', posts[0].content_blocks);
        console.log('[CommunityPage] first post content =', posts[0].content);
        console.log('[CommunityPage] first post content_size =', posts[0].content_size);
        console.log('[CommunityPage] first post content_html_escaped =', posts[0].content_html_escaped);
        console.log('[CommunityPage] first post gallery =', posts[0].gallery);
        console.log('[CommunityPage] first post gallery_urls_count =', posts[0].gallery_urls_count);
        console.log('[CommunityPage] first post gallery_urls_joined =', posts[0].gallery_urls_joined);
        console.log('[CommunityPage] first post gallery_debug_size_raw =', posts[0].gallery_debug_size_raw);
        console.log('[CommunityPage] first post gallery_debug_list_raw =', posts[0].gallery_debug_list_raw);

        // --- DEEP GALLERY DIAGNOSTIC: ALL post-gallery divs + Liquid dump ---
        console.groupCollapsed('[CommunityPage] GALLERY DIAGNOSTIC');
        if (window.__COMMUNITY_DIAG__) {
          console.log('[Liquid dump ALWAYS_RENDERED __COMMUNITY_DIAG__] =', window.__COMMUNITY_DIAG__);
        } else {
          console.warn('[Liquid dump] __COMMUNITY_DIAG__ STILL NOT FOUND — If this persists after refresh, the section file is NOT being applied to the live theme (push via Shopify CLI/Theme Kit or save in Shopify Code Editor).');
        }
        // 2) Scan EVERY post by slug individually
        posts.forEach(function(p, i) {
          var slug = handleize(p.slug);
          console.log('  Post[' + i + '] slug=' + p.slug + ' → handleized=' + slug
            + ' | gallery[] len=' + (p.gallery ? p.gallery.length : 0)
            + ' | gallery_urls_count=' + p.gallery_urls_count
            + ' | gallery_debug_size_raw=' + p.gallery_debug_size_raw);
          var gDiv = document.getElementById('post-gallery-' + slug);
          if (gDiv) {
            var gImgs = gDiv.querySelectorAll('img');
            console.log('    → #post-gallery-' + slug + ' EXISTS. innerHTML (first 300)=' + JSON.stringify(gDiv.innerHTML.substring(0, 300)) + ' | img elements=' + gImgs.length);
            gImgs.forEach(function(g, j) { console.log('      img[' + j + '] src=', g.getAttribute('src')); });
          } else {
            console.warn('    → #post-gallery-' + slug + ' NOT FOUND in DOM.');
          }
        });
        // 3) Dump #diag-post-0-allfields hidden div (first post's ALL field names/values)
        var allFieldsDiag = document.getElementById('diag-post-0-allfields');
        if (allFieldsDiag) {
          console.groupCollapsed('  [HIDDEN DIV] #diag-post-0-allfields (FULL FIELD DUMP)');
          var allFieldsTxt = allFieldsDiag.innerText || allFieldsDiag.textContent;
          console.log('    Full text (first 3000 chars) =', allFieldsTxt.substring(0, 3000));
          // Parse individual fields
          var flds = allFieldsDiag.querySelectorAll('.diag-field');
          console.log('    .diag-field count =', flds.length);
          flds.forEach(function(f, i) {
            var k = f.querySelector('.diag-k');
            var vt = f.querySelector('.diag-v-type');
            var vs = f.querySelector('.diag-v-size');
            var vf = f.querySelector('.diag-v-first100');
            var item0 = f.querySelector('.diag-v-item0json');
            var subs = f.querySelectorAll('[class^="diag-v-item0-subkey"]');
            var line = '    FIELD[' + i + ']: ' + (k ? k.textContent : '?') + ' | type=' + (vt ? vt.textContent : '?') + ' | size=' + (vs ? vs.textContent : '?') + ' | first100=' + (vf ? vf.textContent : '?');
            console.log(line);
            if (item0) console.log('      → item0=' + item0.textContent);
            subs.forEach(function(s) { console.log('      → ' + s.textContent); });
          });
          console.groupEnd();
        } else {
          console.warn('  [HIDDEN DIV] #diag-post-0-allfields NOT FOUND. Hidden div block may not have rendered.');
        }

        // 4) Wildcard scan: any div whose id starts with post-gallery- — EXTRACT DIAG MARKERS
        var allGalleryDivs = document.querySelectorAll('div[id^="post-gallery-"]');
        console.log('  DOM WILDCARD: total <div id="post-gallery-*"> elements found =', allGalleryDivs.length);
        allGalleryDivs.forEach(function(div) {
          var imgs = div.querySelectorAll('img');
          var gSizeDiag = div.querySelector('.diag-gsize');
          var gIterDiags = div.querySelectorAll('.diag-giter');
          console.groupCollapsed('    [' + div.id + '] img count=' + imgs.length + (gSizeDiag ? ' | SIZE_DIAG: ' + gSizeDiag.textContent : ''));
          gIterDiags.forEach(function(d, idx) { console.log('      ' + d.textContent); });
          imgs.forEach(function(img, idx) { console.log('      IMAGE_' + idx + ' src=' + img.getAttribute('src')); });
          console.log('    innerHTML(first 800)=' + JSON.stringify(div.innerHTML.substring(0, 800)));
          console.groupEnd();
        });
        console.groupEnd();

        if (posts[0].content_blocks && posts[0].content_blocks.length > 0) {
          console.log('[CommunityPage] first block text_content =', posts[0].content_blocks[0].text_content);
          console.log('[CommunityPage] first block text_content_html_escaped =', posts[0].content_blocks[0].text_content_html_escaped);
          console.log('[CommunityPage] first block text_content_size =', posts[0].content_blocks[0].text_content_size);
        }
      }

      this.bindEvents();
      this.renderCategories();
      this.checkURL();
      this.initNewsletterPopup();
    },

    bindEvents: function() {
      this.elements.grid.addEventListener('click', this.handleCardClick.bind(this));
      this.elements.detail.addEventListener('click', this.handleBackClick.bind(this));
      window.addEventListener('popstate', this.handlePopState.bind(this));
      if (this.elements.categoriesContainer) {
        this.elements.categoriesContainer.addEventListener('click', this.handleCategoryClick.bind(this));
      }
      // Lightbox: click any image in detail → open full size
      var self = this;
      this.elements.detail.addEventListener('click', function(e) {
        var img = e.target.closest && e.target.closest('img');
        if (!img) return;
        // Only handle images that are part of our gallery content
        var inGallery = img.closest('.community-gallery-grid__item, .community-block--image, figure.community-block');
        if (!inGallery) return;
        // Ignore clicks that were meant to be handled elsewhere (e.g. cards)
        var isInsideCard = img.closest('#community-grid, .community-card');
        if (isInsideCard) return;
        e.preventDefault();
        e.stopPropagation();
        var collected = self.collectGalleryImagesForLightbox(img);
        if (collected.images && collected.images.length > 0) {
          self.openLightbox(collected.images, collected.start);
        }
      });
    },

    getUniqueCategories: function() {
      const categories = new Set();
      this.posts.forEach(post => {
        if (post.category) {
          categories.add(post.category);
        }
      });
      return Array.from(categories).sort();
    },

    renderCategories: function() {
      if (!this.elements.categoriesContainer) return;

      const uniqueCategories = this.getUniqueCategories();
      let html = '';

      // Add "All" category
      const allActive = this.currentCategory === null ? 'sidebar-current-item' : 'sidebar-collection-link';
      html += `<a href="javascript:void(0)" class="${allActive}" data-category="all">All</a>`;

      uniqueCategories.forEach(category => {
        const isActive = this.currentCategory === category;
        const className = isActive ? 'sidebar-current-item' : 'sidebar-collection-link';
        html += `<a href="javascript:void(0)" class="${className}" data-category="${this.escapeHtml(category)}">${this.escapeHtml(category)}</a>`;
      });

      this.elements.categoriesContainer.innerHTML = html;
    },

    handleCategoryClick: function(e) {
      const link = e.target.closest('[data-category]');
      if (!link) return;

      const category = link.getAttribute('data-category');
      this.currentCategory = category === 'all' ? null : category;

      this.renderCategories();
      this.renderGrid();
      this.updateURL();
    },

    checkURL: function() {
      const params = new URLSearchParams(window.location.search);
      const postSlug = params.get('post');
      const categoryParam = params.get('category');

      if (categoryParam && categoryParam !== 'all') {
        this.currentCategory = categoryParam;
      } else {
        this.currentCategory = null;
      }

      this.renderCategories();

      if (postSlug) {
        this.showPost(postSlug, false);
      } else {
        this.renderGrid();
      }
    },

    handleCardClick: function(e) {
      const card = e.target.closest('.community-card');
      if (card) {
        const slug = card.getAttribute('data-slug');
        this.showPost(slug, true);
      }
    },

    handleBackClick: function(e) {
      if (e.target.closest('#community-back-button')) {
        this.showGrid(true);
      }
    },

    handlePopState: function() {
      this.checkURL();
    },

    updateURL: function() {
      const params = new URLSearchParams(window.location.search);
      
      if (this.currentCategory) {
        params.set('category', this.currentCategory);
      } else {
        params.delete('category');
      }

      if (this.currentPost) {
        params.set('post', this.currentPost.slug);
      } else {
        params.delete('post');
      }

      const url = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      history.replaceState(null, '', url);
    },

    showPost: function(slug, updateHistory) {
      const post = this.posts.find(p => p.slug === slug);

      if (!post) {
        this.showGrid(updateHistory);
        return;
      }

      this.currentPost = post;

      if (updateHistory) {
        this.updateURL();
      }

      // Update sidebar
      if (this.elements.communitySidebarPost) {
        this.elements.communitySidebarPost.textContent = post.title;
        this.elements.communitySidebarPost.style.display = 'block';
      }

      this.renderDetail(post);
      this.switchState('detail');
      window.scrollTo(0, 0);
    },

    showGrid: function(updateHistory) {
      this.currentPost = null;

      if (updateHistory) {
        this.updateURL();
      }

      // Update sidebar
      if (this.elements.communitySidebarPost) {
        this.elements.communitySidebarPost.style.display = 'none';
      }

      this.renderGrid();
      this.switchState('grid');
    },

    renderGrid: function() {
      const gridContainer = this.elements.grid.querySelector('.community-grid__container');
      if (!gridContainer) return;

      let filteredPosts = this.posts;
      if (this.currentCategory) {
        filteredPosts = this.posts.filter(p => p.category === this.currentCategory);
      }

      // Smart arrangement: set grid columns based on post count
      gridContainer.className = 'community-grid__container';
      if (filteredPosts.length === 1) {
        gridContainer.classList.add('grid-1-col');
      } else if (filteredPosts.length === 2) {
        gridContainer.classList.add('grid-2-col');
      } else if (filteredPosts.length >= 3) {
        gridContainer.classList.add('grid-3-col');
      }

      let html = '';
      if (filteredPosts.length === 0) {
        html = `<div class="community-empty">No posts found in this category.</div>`;
      } else {
        filteredPosts.forEach(post => {
          html += this.renderCard(post);
        });
      }

      gridContainer.innerHTML = html;
    },

    switchState: function(state) {
      if (state === 'grid') {
        this.elements.grid.classList.remove('state-hidden');
        this.elements.grid.classList.add('state-visible');
        this.elements.detail.classList.remove('state-visible');
        this.elements.detail.classList.add('state-hidden');
      } else {
        this.elements.grid.classList.remove('state-visible');
        this.elements.grid.classList.add('state-hidden');
        this.elements.detail.classList.remove('state-hidden');
        this.elements.detail.classList.add('state-visible');
      }
    },

    renderDetail: function(post) {
      const self = this;
      const allUsedIndices = new Set();
      const resolved = resolvePostContentHTML(post);
      const effectiveGallery = (resolved.galleryImages && resolved.galleryImages.length > 0)
        ? resolved.galleryImages
        : (post.gallery || []);

      let html = `
        <article class="community-post-detail">
          <button class="community-post-detail__back" id="community-back-button">Community</button>
      `;

      if (post.hero_image) {
        html += `
          <div class="community-post-detail__hero">
            <img src="${post.hero_image}" alt="${this.escapeHtml(post.title)}" loading="lazy">
          </div>
        `;
      }

      html += `
        <div class="community-post-detail__content">
          <h1 class="community-post-detail__title" style="text-align:left !important;">${this.escapeHtml(post.title)}</h1>
          <div class="community-post-detail__meta" style="display:flex; flex-direction:column; align-items:flex-start !important; justify-content:flex-start !important; text-align:left !important;">
      `;

      if (post.location) {
        html += `<p class="community-post-detail__location" style="text-align:left !important; display:block; width:100%;">${this.escapeHtml(post.location)}</p>`;
      }

      if (post.date) {
        html += `<p class="community-post-detail__date" style="text-align:left !important; display:block; width:100%;">${this.formatDate(post.date)}</p>`;
      }

      html += `</div>`;

      let contentRendered = false;
      let richTextBody = '';

      // --- Tier A: resolved content from server div / escaped HTML / AST / post.content ---
      if (resolved.html && resolved.html.trim().length > 0) {
        const { parsedContent, galleryUsed, usedIndices } = self.parseContent(resolved.html, effectiveGallery);
        if (galleryUsed) usedIndices.forEach(i => allUsedIndices.add(i));
        if (parsedContent && parsedContent.trim().length > 0) {
          richTextBody += parsedContent;
        }
      }

      // --- Tier B: content_blocks, if any produced real text / images / videos ---
      if (post.content_blocks && Array.isArray(post.content_blocks) && post.content_blocks.length > 0) {
        const sortedBlocks = [...post.content_blocks].sort((a, b) => {
          return (a.order || 0) - (b.order || 0);
        });

        let blocksHtml = '';
        sortedBlocks.forEach(block => {
          const blockType = (block.type || 'text').toLowerCase().trim();
          let blockOutput = '';
          const alignment = (block.alignment || 'left').toLowerCase().trim();
          const alignClass = (alignment === 'center' || alignment === 'centre' || alignment === 'middle')
            ? 'align-center'
            : (alignment === 'right' ? 'align-right' : 'align-left');

          // Resolve block text with 3-tier fallback + normalize to HTML (handles stringified AST)
          let blockTextRaw = '';
          if (block.text_content_html_escaped && typeof block.text_content_html_escaped === 'string' && block.text_content_html_escaped.trim().length > 0) {
            blockTextRaw = decodeHtmlEntities(block.text_content_html_escaped);
          } else if (block.text_content) {
            blockTextRaw = block.text_content;
          }
          let blockText = normalizeToHTML(blockTextRaw);

          // --- 1) TEXT block ---
          if ((blockType === 'text' || blockType === 'paragraph') && blockText && blockText.trim() !== '') {
            const { parsedContent, galleryUsed, usedIndices } = self.parseContent(blockText, effectiveGallery);
            if (galleryUsed) usedIndices.forEach(i => allUsedIndices.add(i));
            if (parsedContent && parsedContent.trim() !== '') {
              blockOutput += `<div class="community-block community-block--text ${alignClass}">${parsedContent}</div>`;
            }
          }

          // --- 2) HEADING block ---
          if (blockType === 'heading' || blockType === 'headline' || blockType.startsWith('h')) {
            let level = 2;
            if (block.heading_level) {
              const parsedLv = parseInt(String(block.heading_level).replace(/\D/g, ''), 10);
              if (!isNaN(parsedLv) && parsedLv >= 1 && parsedLv <= 6) level = parsedLv;
            } else if (blockType.charAt(0) === 'h' && /[1-6]/.test(blockType.charAt(1))) {
              level = parseInt(blockType.charAt(1), 10);
            }
            const headingTextRaw = blockText || '';
            if (headingTextRaw.trim() !== '') {
              const { parsedContent, galleryUsed, usedIndices } = self.parseContent(headingTextRaw, effectiveGallery);
              if (galleryUsed) usedIndices.forEach(i => allUsedIndices.add(i));
              const stripped = (parsedContent || '')
                .replace(/<\/?p[^>]*>/gi, '')
                .replace(/<br\s*\/?>/gi, ' ')
                .trim();
              blockOutput += `<div class="community-block community-block--heading ${alignClass}"><h${level}>${stripped || headingTextRaw}</h${level}></div>`;
            }
          }

          // --- 3) IMAGE block (with size, caption, alignment, figure wrapper) ---
          if (blockType === 'image' && block.image) {
            const imgAlt = block.image_alt ? self.escapeHtml(block.image_alt) : 'Community post image';
            const captionHtml = block.caption ? `<figcaption class="community-caption">${self.escapeHtml(block.caption)}</figcaption>` : '';

            // Image size class mapping
            const imgSizeRaw = (block.image_size || 'large').toLowerCase().replace(/\s+/g, '-').trim();
            let imgSizeClass = 'img-lg';
            switch (imgSizeRaw) {
              case 'sm':
              case 'small':  imgSizeClass = 'img-sm'; break;
              case 'md':
              case 'med':
              case 'medium': imgSizeClass = 'img-md'; break;
              case 'lg':
              case 'large':  imgSizeClass = 'img-lg'; break;
              case 'fw':
              case 'full':
              case 'full-width':
              case 'fullwidth': imgSizeClass = 'img-full'; break;
              default: imgSizeClass = 'img-lg';
            }

            const wrapClasses = `community-block community-block--image ${alignClass} ${imgSizeClass}`;
            const imgHtml = `<img src="${block.image}" alt="${imgAlt}" loading="lazy">`;

            if (captionHtml) {
              blockOutput += `<figure class="${wrapClasses}">${imgHtml}${captionHtml}</figure>`;
            } else {
              blockOutput += `<div class="${wrapClasses}">${imgHtml}</div>`;
            }
          }

          // --- 4) QUOTE block ---
          if ((blockType === 'quote' || blockType === 'blockquote') && blockText && blockText.trim() !== '') {
            const { parsedContent, galleryUsed, usedIndices } = self.parseContent(blockText, effectiveGallery);
            if (galleryUsed) usedIndices.forEach(i => allUsedIndices.add(i));
            if (parsedContent && parsedContent.trim() !== '') {
              blockOutput += `<div class="community-block community-block--quote ${alignClass}"><blockquote>${parsedContent}</blockquote></div>`;
            }
          }

          // --- 5) VIDEO block ---
          if (blockType === 'video' && block.video_url) {
            const videoUrl = block.video_url.trim();
            let embedUrl = videoUrl;

            if (videoUrl.includes('youtube.com/watch?v=')) {
              const ytId = videoUrl.split('v=')[1].split('&')[0];
              embedUrl = `https://www.youtube.com/embed/${ytId}`;
            } else if (videoUrl.includes('youtu.be/')) {
              const ytId = videoUrl.split('youtu.be/')[1].split('?')[0];
              embedUrl = `https://www.youtube.com/embed/${ytId}`;
            } else if (videoUrl.includes('vimeo.com/')) {
              const vimeoId = videoUrl.split('vimeo.com/')[1].split('?')[0];
              embedUrl = `https://player.vimeo.com/video/${vimeoId}`;
            }

            const iframeHtml = `<iframe src="${embedUrl}" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen loading="lazy"></iframe>`;
            blockOutput += `<div class="community-block community-block--video ${alignClass}">${iframeHtml}</div>`;
          }

          // --- 6) DIVIDER block ---
          if (blockType === 'divider' || blockType === 'hr' || blockType === 'separator' || blockType === 'spacer') {
            blockOutput += `<div class="community-block community-block--divider ${alignClass}"><hr></div>`;
          }

          blocksHtml += blockOutput;
        });

        blocksHtml = blocksHtml.trim();
        if (blocksHtml.length > 0) {
          richTextBody += blocksHtml;
        }
      }

      // --- Append any UNUSED gallery images into the content body inside a responsive grid ---
      if (effectiveGallery && effectiveGallery.length > 0) {
        let unusedItems = [];
        effectiveGallery.forEach((img, idx) => {
          if (img && !allUsedIndices.has(idx)) {
            unusedItems.push({ img: img, idx: idx });
          }
        });
        if (unusedItems.length > 0) {
          let gridItems = '';
          unusedItems.forEach(item => {
            gridItems += `
              <div class="community-gallery-grid__item">
                <img src="${item.img}" alt="Gallery image ${item.idx + 1}" loading="lazy">
              </div>
            `;
          });
          richTextBody += `<div class="community-gallery-grid">${gridItems}</div>`;
        }
      }

      richTextBody = richTextBody.trim();
      if (richTextBody.length > 0) {
        html += `<div class="community-post-detail__rich-text">${richTextBody}</div>`;
        contentRendered = true;
      }

      if (!contentRendered) {
        console.warn('[CommunityPage] renderDetail: NO RICH TEXT CONTENT for post', post.slug, '| resolved.sourceUsed=', resolved.sourceUsed, '| resolved.html.length=', resolved.html.length, '| blocks=', post.content_blocks ? post.content_blocks.length : 0);
      }

      // All gallery images are now embedded directly into the content body above.
      
      html += `</div></article>`;

      this.elements.detail.innerHTML = html;
    },

    parseContent: function(content, gallery) {
      if (!content) return { parsedContent: '', galleryUsed: false, usedIndices: new Set() };
      
      let parsed = content;
      let galleryUsedInline = false;
      const usedIndices = new Set();
      const self = this;

      // --- Gallery Shortcodes: [gallery 0], [gallery 1 alt="Alt"], [gallery 2 width="50%"], etc. ---
      
      // [gallery N alt="Custom Alt" width="50%"]
      parsed = parsed.replace(/\[gallery\s+(\d+)\s+alt="([^"]*)"\s+width="([^"]*)"\s*\]/g, function(match, indexStr, altText, width) {
        const index = parseInt(indexStr, 10);
        if (gallery && gallery[index]) {
          galleryUsedInline = true;
          usedIndices.add(index);
          const cleanAlt = altText.trim() || `Gallery image ${index + 1}`;
          const cleanWidth = width.trim() || '100%';
          return `<img src="${gallery[index]}" alt="${self.escapeHtml(cleanAlt)}" loading="lazy" style="width:${self.escapeHtml(cleanWidth)}; max-width:100%;">`;
        }
        return match;
      });

      // [gallery N width="50%"]
      parsed = parsed.replace(/\[gallery\s+(\d+)\s+width="([^"]*)"\s*\]/g, function(match, indexStr, width) {
        const index = parseInt(indexStr, 10);
        if (gallery && gallery[index]) {
          galleryUsedInline = true;
          usedIndices.add(index);
          const cleanWidth = width.trim() || '100%';
          return `<img src="${gallery[index]}" alt="Gallery image ${index + 1}" loading="lazy" style="width:${self.escapeHtml(cleanWidth)}; max-width:100%;">`;
        }
        return match;
      });

      // [gallery N alt="Custom Alt"]
      parsed = parsed.replace(/\[gallery\s+(\d+)\s+alt="([^"]*)"\s*\]/g, function(match, indexStr, altText) {
        const index = parseInt(indexStr, 10);
        if (gallery && gallery[index]) {
          galleryUsedInline = true;
          usedIndices.add(index);
          const cleanAlt = altText.trim() || `Gallery image ${index + 1}`;
          return `<img src="${gallery[index]}" alt="${self.escapeHtml(cleanAlt)}" loading="lazy">`;
        }
        return match;
      });

      // [gallery N]
      parsed = parsed.replace(/\[gallery\s+(\d+)\s*\]/g, function(match, indexStr) {
        const index = parseInt(indexStr, 10);
        if (gallery && gallery[index]) {
          galleryUsedInline = true;
          usedIndices.add(index);
          return `<img src="${gallery[index]}" alt="Gallery image ${index + 1}" loading="lazy">`;
        }
        return match;
      });
      
      // Parse Markdown-style images: ![Alt Text](URL)
      parsed = parsed.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, function(match, altText, url) {
        const cleanUrl = url.trim();
        const cleanAlt = altText.trim() || 'Community post image';
        return `<img src="${cleanUrl}" alt="${cleanAlt}" loading="lazy">`;
      });
      
      // Parse shortcode: [img alt="Alt Text" width="50%"]URL[/img]
      parsed = parsed.replace(/\[img\s+alt="([^"]*)"\s+width="([^"]*)"\]([\s\S]*?)\[\/img\]/g, function(match, altText, width, url) {
        const cleanUrl = url.trim();
        const cleanAlt = altText.trim() || 'Community post image';
        return `<img src="${cleanUrl}" alt="${cleanAlt}" loading="lazy" style="width:${width};">`;
      });
      
      // Parse shortcode: [img width="50%"]URL[/img]
      parsed = parsed.replace(/\[img\s+width="([^"]*)"\]([\s\S]*?)\[\/img\]/g, function(match, width, url) {
        const cleanUrl = url.trim();
        return `<img src="${cleanUrl}" alt="Community post image" loading="lazy" style="width:${width};">`;
      });
      
      // Parse shortcode: [img alt="Alt Text"]URL[/img]
      parsed = parsed.replace(/\[img\s+alt="([^"]*)"\]([\s\S]*?)\[\/img\]/g, function(match, altText, url) {
        const cleanUrl = url.trim();
        const cleanAlt = altText.trim() || 'Community post image';
        return `<img src="${cleanUrl}" alt="${cleanAlt}" loading="lazy">`;
      });
      
      // Parse shortcode: [img]URL[/img]
      parsed = parsed.replace(/\[img\]([\s\S]*?)\[\/img\]/g, function(match, url) {
        const cleanUrl = url.trim();
        return `<img src="${cleanUrl}" alt="Community post image" loading="lazy">`;
      });
      
      return {
        parsedContent: parsed,
        galleryUsed: galleryUsedInline,
        usedIndices: usedIndices
      };
    },

    renderCard: function(post) {
      let html = `<article class="community-card" data-slug="${this.escapeHtml(post.slug)}">`;

      if (post.thumbnail) {
        html += `
          <div class="community-card__image">
            <img src="${post.thumbnail}" alt="${this.escapeHtml(post.title)}" loading="lazy">
          </div>
        `;
      }

      html += `
        <div class="community-card__content">
          <h3 class="community-card__title">${this.escapeHtml(post.title)}</h3>
      `;

      if (post.location) {
        html += `<p class="community-card__location">${this.escapeHtml(post.location)}</p>`;
      }

      if (post.date) {
        html += `<p class="community-card__date">${this.formatDate(post.date)}</p>`;
      }

      html += `</div></article>`;
      return html;
    },

    formatDate: function(dateString) {
      if (!dateString) return '';

      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return dateString; // Return as-is if invalid date
        }
        const options = { year: 'numeric', month: 'long' };
        return date.toLocaleDateString('en-US', options);
      } catch (e) {
        return dateString;
      }
    },

    escapeHtml: function(text) {
      if (!text) return '';
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },

    /** Lightbox: open full-size image modal */
    openLightbox: function(images, startIndex) {
      if (!images || images.length === 0) return;
      this.lightbox = this.lightbox || {};
      this.lightbox.images = images.slice();
      this.lightbox.index = (typeof startIndex === 'number' && startIndex >= 0 && startIndex < images.length) ? startIndex : 0;

      // Ensure modal DOM exists
      let modal = document.getElementById('community-lightbox');
      if (!modal) {
        modal = document.createElement('div');
        modal.id = 'community-lightbox';
        modal.className = 'community-lightbox';
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        modal.innerHTML = `
          <button class="community-lightbox__close" aria-label="Close">&times;</button>
          <button class="community-lightbox__nav community-lightbox__nav--prev" aria-label="Previous">&#10094;</button>
          <button class="community-lightbox__nav community-lightbox__nav--next" aria-label="Next">&#10095;</button>
          <div class="community-lightbox__stage">
            <img class="community-lightbox__img" alt="">
          </div>
          <div class="community-lightbox__counter"></div>
        `;
        document.body.appendChild(modal);

        // Wire up modal controls once
        const self = this;
        modal.querySelector('.community-lightbox__close').addEventListener('click', function() { self.closeLightbox(); });
        modal.querySelector('.community-lightbox__nav--prev').addEventListener('click', function(e) { e.stopPropagation(); self.navigateLightbox(-1); });
        modal.querySelector('.community-lightbox__nav--next').addEventListener('click', function(e) { e.stopPropagation(); self.navigateLightbox(1); });
        modal.addEventListener('click', function(e) { if (e.target === modal) self.closeLightbox(); });

        document.addEventListener('keydown', function(e) {
          const lb = document.getElementById('community-lightbox');
          if (!lb || !lb.classList.contains('is-open')) return;
          if (e.key === 'Escape') self.closeLightbox();
          else if (e.key === 'ArrowLeft') self.navigateLightbox(-1);
          else if (e.key === 'ArrowRight') self.navigateLightbox(1);
        });
      }

      this.updateLightboxImg();
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    },

    /** Lightbox: close */
    closeLightbox: function() {
      const modal = document.getElementById('community-lightbox');
      if (modal) modal.classList.remove('is-open');
      document.body.style.overflow = '';
    },

    /** Lightbox: change index / refresh image */
    navigateLightbox: function(delta) {
      if (!this.lightbox || !this.lightbox.images || this.lightbox.images.length === 0) return;
      const total = this.lightbox.images.length;
      this.lightbox.index = (((this.lightbox.index + delta) % total) + total) % total;
      this.updateLightboxImg();
    },

    updateLightboxImg: function() {
      if (!this.lightbox) return;
      const modal = document.getElementById('community-lightbox');
      if (!modal) return;
      const imgEl = modal.querySelector('.community-lightbox__img');
      const counter = modal.querySelector('.community-lightbox__counter');
      const src = this.lightbox.images[this.lightbox.index];
      imgEl.src = src;
      imgEl.alt = 'Gallery image ' + (this.lightbox.index + 1);
      counter.textContent = (this.lightbox.index + 1) + ' / ' + this.lightbox.images.length;
    },

    /** Collect ALL gallery images on a detail view (shortcode-placed + grid-appended) into ordered list */
    collectGalleryImagesForLightbox: function(triggerImg) {
      const ordered = [];
      let startIndex = 0;
      const detail = this.elements.detail;
      if (!detail) return { images: [], start: 0 };

      const allImgs = detail.querySelectorAll('.community-gallery-grid__item img, .community-block--image img, figure.community-block img');
      const triggerSrc = triggerImg ? (triggerImg.getAttribute('src') || triggerImg.src || '') : '';

      allImgs.forEach(function(img, i) {
        const s = img.getAttribute('src') || img.src || '';
        if (!s) return;
        ordered.push(s);
        if (triggerSrc && (s === triggerSrc || s.split('?')[0] === triggerSrc.split('?')[0])) {
          startIndex = ordered.length - 1;
        }
      });

      return { images: ordered, start: startIndex };
    },

    /** Newsletter popup — exit-intent, first-view only, localStorage guard */

    NEWSLETTER_POPUP_SEEN_KEY: 'cultur_newsletter_popup_seen_v1',
    NEWSLETTER_POPUP_SUBSCRIBED_KEY: 'cultur_newsletter_subscribed_v1',
    NEWSLETTER_POPUP_FALLBACK_MS: 20000,

    initNewsletterPopup: function() {
      this.newsletter = this.newsletter || {};
      this.newsletter.popup = document.getElementById('newsletter-popup');
      this.newsletter.shown = false;

      if (!this.newsletter.popup) return;

      // Already seen or subscribed → never show
      try {
        if (localStorage.getItem(this.NEWSLETTER_POPUP_SEEN_KEY) === '1' ||
            localStorage.getItem(this.NEWSLETTER_POPUP_SUBSCRIBED_KEY) === '1') {
          return;
        }
      } catch (e) { /* ignore storage errors */ }

      const self = this;

      // 1) Strong desktop exit-intent:
      // Track the cursor while it is still inside the page and fire as it
      // heads into the browser tab / close-button chrome.
      var lastY = null;
      var lastT = null;
      var topBandPx = 72;
      var closeZonePx = 220;
      var tabZonePx = 340;

      var onMouseMove = function(e) {
        if (self.newsletter.shown) return;

        var now = Date.now();
        var currentY = typeof e.clientY === 'number' ? e.clientY : 9999;
        var currentX = typeof e.clientX === 'number' ? e.clientX : 0;

        if (lastY !== null && lastT !== null) {
          var dt = now - lastT;
          var dy = currentY - lastY;
          var vy = dt > 0 ? (dy / dt) : 0;
          var inTopBand = currentY <= topBandPx;
          var inCloseZone = currentY <= 28 && currentX >= (window.innerWidth - closeZonePx);
          var inTabZone = currentY <= 36 && currentX <= tabZonePx;
          var movingUpFast = vy < -0.12;
          var movingUp = dy < -10;

          if (
            inCloseZone ||
            inTabZone ||
            (inTopBand && movingUpFast) ||
            (currentY <= 18 && movingUp)
          ) {
            self.showNewsletterPopup();
            return;
          }
        }

        lastY = currentY;
        lastT = now;
      };

      var onMouseLeave = function(e) {
        if (self.newsletter.shown) return;
        if (e.clientY <= 10) {
          self.showNewsletterPopup();
        }
      };

      document.documentElement.addEventListener('mousemove', onMouseMove);
      document.documentElement.addEventListener('mouseleave', onMouseLeave);

      // 2) Tab switching / new-tab intent:
      // visibilitychange is too late to show before the tab switches, but it
      // still helps catch cases where the user comes right back.
      var hiddenOnce = false;
      document.addEventListener('visibilitychange', function() {
        if (self.newsletter.shown) return;
        if (document.visibilityState === 'hidden') {
          hiddenOnce = true;
          return;
        }
        if (hiddenOnce && document.visibilityState === 'visible') {
          self.showNewsletterPopup();
        }
      });

      // 3) Mobile fallback + catch-all: show after 20s if not shown yet
      this.newsletter.fallbackTimer = setTimeout(function() {
        self.showNewsletterPopup();
      }, this.NEWSLETTER_POPUP_FALLBACK_MS);

      // 4) Close controls (button + backdrop)
      const closeEls = this.newsletter.popup.querySelectorAll('[data-newsletter-close]');
      closeEls.forEach(function(el) {
        el.addEventListener('click', function() { self.hideNewsletterPopup(); });
      });

      // 5) Esc key close
      document.addEventListener('keydown', function(e) {
        if (!self.newsletter.shown) return;
        if (e.key === 'Escape') self.hideNewsletterPopup();
      });

      // 6) Form submit → mark subscribed
      const form = this.newsletter.popup.querySelector('[data-newsletter-form]');
      if (form) {
        form.addEventListener('submit', function(e) {
          e.preventDefault();
          const input = form.querySelector('input[type="email"]');
          const status = form.querySelector('[data-newsletter-status]');
          const submitBtn = form.querySelector('button[type="submit"]');
          const email = input ? input.value.trim() : '';
          const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
          if (!ok) {
            if (status) {
              status.textContent = 'Please enter a valid email address.';
              status.className = 'newsletter-popup__status is-error';
              status.hidden = false;
            }
            if (input) input.focus();
            return;
          }
          if (submitBtn) submitBtn.disabled = true;
          try {
            localStorage.setItem(self.NEWSLETTER_POPUP_SUBSCRIBED_KEY, '1');
          } catch (e) { /* ignore */ }
          if (status) {
            status.textContent = 'Thanks for subscribing! Welcome to the community.';
            status.className = 'newsletter-popup__status is-success';
            status.hidden = false;
          }
          // Let user read success message briefly then close
          setTimeout(function() { self.hideNewsletterPopup(); }, 1600);
        });
      }
    },

    showNewsletterPopup: function() {
      if (!this.newsletter || !this.newsletter.popup || this.newsletter.shown) return;
      this.newsletter.shown = true;
      if (this.newsletter.fallbackTimer) {
        clearTimeout(this.newsletter.fallbackTimer);
        this.newsletter.fallbackTimer = null;
      }
      try {
        localStorage.setItem(this.NEWSLETTER_POPUP_SEEN_KEY, '1');
      } catch (e) { /* ignore */ }
      this.newsletter.popup.hidden = false;
      const emailInput = this.newsletter.popup.querySelector('input[type="email"]');
      if (emailInput) {
        try { emailInput.focus({ preventScroll: true }); } catch (_) { emailInput.focus(); }
      }
      document.body.style.overflow = 'hidden';
    },

    hideNewsletterPopup: function() {
      if (!this.newsletter || !this.newsletter.popup) return;
      this.newsletter.shown = true; // mark as interacted so we don't re-trigger
      this.newsletter.popup.hidden = true;
      document.body.style.overflow = '';
    }
  };
})();
